import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import AdminLayout from "../../layout/AdminLayout";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/MeetingsModel";
import { useNotificationCount } from "../../../context/NotificationCountContext";
import MeetingTable from "./MeetingTable";
import { ConfirmDelete } from "../../modals/ConfirmDelete";
import { apiError } from "../../../helpers/ErrorHelper";
import { toast } from "react-toastify";
import PageHeader from "../../common/PageHeader";
import config from "../../../config/config";
import { getData as getClients } from "../../../models/ClientsModel";
import { getData as getContacts } from "../../../models/ContactsModel";
import { getManagers } from "../../../models/UserModel";
import { MyReactSelect } from "../../inputs/inputs";
import { useDebouncedCallback } from "use-debounce";
import { checkPermission, isSuperAdminUser } from "../../../helpers/GeneralHelper";

const MODULE = {
  title: "Meetings",
  singularTitle: "Meeting",
  key: "meetings",
  route: "meetings",
  primaryCol: "_meeting_id",
};

export default function Meetings() {
  const module = MODULE;
  const { state } = useAuth();
  let user_data = state.user_data;
  const isAdmin =
    user_data?.urole_type === "admin" || isSuperAdminUser(user_data);
  const [searchParams] = useSearchParams();
  var _client_id = searchParams.get("_client_id");
  const navigate = useNavigate();
  const { setNotificationCount } = useNotificationCount();

  const [singleData, setSingleData] = useState({});
  const openUpdateModal = (data) => {
    navigate(`/meetings/${data.meeting_unique_id}/edit`, {
      state: { data, module },
    });
  };

  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const openDeleteModal = (data) => {
    setSingleData(data);
    setToggleDeleteModal(true);
  };

  const [loading, setLoading] = useState(true);

  const deleteData = async () => {
    setLoading(true);
    try {
      const response = await model.remove(
        user_data.user_token,
        singleData[module.primaryCol]
      );
      if (response.status) {
        setNotificationCount(localStorage.getItem("unread_notification_count"));
        toast.success(response.message);
        model.saveToStorage(
          response.data[module.key],
          response.data.pagination
        );
        refreshData();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
      if (error.response?.data.invalid_token == 1) {
        navigate("/re-login");
      }
    }
    setToggleDeleteModal(false);
    setLoading(false);
  };

  const handleView = (data) => {
    navigate(`/meetings/${data.meeting_unique_id}/view`, {
      state: { data, module },
    });
  };

  const flashData = async () => {
    localStorage.removeItem(module.key);
    fetchData(limit, page);
  };

  const [filters, setFilters] = useState({
    meeting_logged_by: "",
    meeting_client_id: "",
    meeting_contact_id: "",
  });

  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const result = await getClients({
          user_token: user_data.user_token,
          page: 1,
          limit: -1,
        });
        if (!result?.clients) return;
        setClients(
          result.clients.map((client) => ({
            value: client._client_id,
            label: client.client_name,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchManagersList = async () => {
      try {
        localStorage.removeItem("managers");
        const response = await getManagers({
          user_token: user_data.user_token,
        });
        if (!response?.managers) return;
        setManagers(
          response.managers
            .map((item) => {
              const name = item.manager_name || `${item.user_first_name || ""} ${item.user_last_name || ""}`.trim();
              const designation = item.designation_name ? ` (${item.designation_name})` : "";
              return {
                value: item._user_id,
                label: `${name}${designation}`,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }))
        );
      } catch (error) {
        console.error(error);
      }
    };
    fetchManagersList();
  }, [isAdmin]);

  useEffect(() => {
    const fetchContacts = async () => {
      const clientId =
        filters.meeting_client_id?.value ||
        filters.meeting_client_id ||
        _client_id;
      if (!clientId) {
        setFilters((prev) => ({ ...prev, meeting_contact_id: "" }));
        setContacts([]);
        return;
      }
      try {
        const response = await getContacts({
          user_token: user_data.user_token,
          contact_client_id: clientId,
          page: 1,
          limit: -1,
        });
        if (typeof response != typeof undefined) {
          const options = (response.client_contacts || []).map((item) => ({
            value: item._contact_id,
            label: item.contact_name,
          }));
          setContacts(options);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchContacts();
  }, [filters.meeting_client_id]);

  const [isFilterReset, setIsFilterReset] = useState(false);
  useEffect(() => {
    setIsFilterReset(false);
    Object.keys(filters).forEach((item) => {
      if (filters[item]) {
        setIsFilterReset(true);
      }
    });
    fetchData(10, 1);
  }, [filters]);

  const resetFilter = () => {
    let blankFilter = {};
    Object.keys(filters).forEach((item) => {
      blankFilter[item] = "";
    });
    setFilters(blankFilter);
  };

  const [listData, setListData] = useState([]);
  const [cacheTime, setCacheTime] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const debounced = useDebouncedCallback(() => fetchData(limit, page), 500);

  const fetchData = async (newLimit, page) => {
    setLoading(true);
    const clientId =
      filters.meeting_client_id?.value ||
      filters.meeting_client_id ||
      _client_id ||
      "";
    const contactId =
      filters.meeting_contact_id?.value || filters.meeting_contact_id || "";
    const loggedById =
      filters.meeting_logged_by?.value || filters.meeting_logged_by || "";

    try {
      const response = await model.getData({
        search: searchQuery,
        page: page,
        limit: newLimit,
        user_token: user_data.user_token,
        meeting_client_id: clientId,
        meeting_contact_id: contactId,
        ...(isAdmin && loggedById ? { meeting_logged_by: loggedById } : {}),
      });
      if (typeof response == typeof undefined) {
        navigate("/re-login");
        return;
      }

      setListData(response[module.key] || []);
      setTotalRows(response.pagination?.total_rows || 0);
      setLimit(newLimit);
      setPage(page);
      setNotificationCount(localStorage.getItem("unread_notification_count"));
      if (response.time) {
        setCacheTime(response.time);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const refreshData = async () => {
    fetchData(limit, page);
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>{module.title} - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid">
          <PageHeader module={module} />

          <div className="row">
            <div className="col-xl-12">
              <div className="card custom-card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div
                    className="card-title fw-normal"
                    style={{ textTransform: "unset" }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {isAdmin && (
                        <>
                          <MyReactSelect
                            column="meeting_logged_by"
                            options={managers}
                            firstOption="Select AM"
                            fields={filters}
                            setFields={setFilters}
                          />
                          &nbsp;&nbsp;
                        </>
                      )}
                      <MyReactSelect
                        column="meeting_client_id"
                        options={clients}
                        firstOption="Select account"
                        fields={filters}
                        setFields={setFilters}
                      />
                      &nbsp;&nbsp;
                      <MyReactSelect
                        column="meeting_contact_id"
                        options={contacts}
                        firstOption="Select POC"
                        fields={filters}
                        setFields={setFilters}
                      />
                      &nbsp;&nbsp;
                      {isFilterReset && (
                        <button
                          className="btn btn-outline-dark"
                          onClick={resetFilter}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <div>
                      <input
                        type="text"
                        className="form-control table-search"
                        onChange={(e) => {
                          debounced(e.target.value);
                          setSearchQuery(e.target.value);
                        }}
                        placeholder="Search..."
                        autoComplete="off"
                      />

                      {checkPermission(module.route, "add") && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            navigate(`/meetings/create`, {
                              state: { module },
                            })
                          }
                        >
                          <i className="ri-add-line me-1 fw-semibold align-middle"></i>
                          Add Meeting
                        </button>
                      )}
                    </div>

                    {config.isCache ? (
                      <button
                        className="btn btn-success-light btn-sm"
                        onClick={() => flashData()}
                      >
                        <i className="ri-refresh-line me-1 fw-semibold align-middle"></i>{" "}
                        Refresh
                        {cacheTime ? ` (${cacheTime})` : ""}
                      </button>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <MeetingTable
                      module={module}
                      data={listData}
                      loading={loading}
                      totalRows={totalRows}
                      page={page}
                      limit={limit}
                      fetchData={fetchData}
                      openUpdateModal={openUpdateModal}
                      openDeleteModal={openDeleteModal}
                      handleView={handleView}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ConfirmDelete
            module={module}
            showModal={toggleDeleteModal}
            setShowModal={setToggleDeleteModal}
            loading={loading}
            onConfirm={deleteData}
          />
        </div>
      </div>

      <Helmet></Helmet>
    </AdminLayout>
  );
}
